import { BotType } from "@shared/types";
import {
  BotType as AnalyticsBotType,
  BotTypeValueType,
} from "analytics/events";
import { mapValues } from "lodash";
import {
  BASE_DCA_BOTS_PATH,
  HEDGING_BOT_PATH,
  GRID_BOT_PATH,
  SIGNALS_BOT_PATH,
  TRADING_VIEW_BOT_PATH,
  TRADING_VIEW_SIGNALS_PATH,
} from "common/constants";
import { BotsTab } from "@shared/features/bots/types";

export const BOT_TYPE_ANALYTICS_MAPPING: Record<BotType, BotTypeValueType> = {
  [BotType.ASAP]: AnalyticsBotType.DCA,
  [BotType.TRADING_VIEW]: AnalyticsBotType.TRADING_VIEW,
  [BotType.SIGNAL]: AnalyticsBotType.SIGNALS,
  [BotType.GRID]: AnalyticsBotType.GRID,
  [BotType.TRADING_VIEW_SIGNALS]: AnalyticsBotType.TRADING_VIEW,
  [BotType.HEDGING]: AnalyticsBotType.DCA,
};

export const BOT_TYPE_TO_BASE_PATH: Record<BotType, string> = {
  [BotType.ASAP]: BASE_DCA_BOTS_PATH,
  [BotType.TRADING_VIEW]: TRADING_VIEW_BOT_PATH,
  [BotType.SIGNAL]: SIGNALS_BOT_PATH,
  [BotType.GRID]: GRID_BOT_PATH,
  [BotType.TRADING_VIEW_SIGNALS]: TRADING_VIEW_SIGNALS_PATH,
  [BotType.HEDGING]: HEDGING_BOT_PATH,
};

export const BOT_TYPE_TO_BASE_BACKTESTING_PATH: Record<BotType, string> =
  mapValues(BOT_TYPE_TO_BASE_PATH, (path) => path + "/backtesting");
export const BOT_TYPE_TO_BASE_SIGNALS_PATH: Record<BotType, string> = mapValues(
  BOT_TYPE_TO_BASE_PATH,
  (path) => path + "/user-signals"
);
export const SIGNAL_BOTS_SIGNALS_PATH =
  BOT_TYPE_TO_BASE_SIGNALS_PATH[BotType.SIGNAL];
export const BOT_TYPE_TO_BASE_MY_BOTS_PATH: Record<BotType, string> = mapValues(
  BOT_TYPE_TO_BASE_PATH,
  (path) => path + `/${BotsTab.MY_BOTS}`
);

export const BOTS_PAGE_LAYOUT_CHANGE_KEY = "bots_page_layout";

export const BOT_TYPE_TO_DESCRIPTION = {
  [BotType.SIGNAL]: "descriptions.signals_bot",
  [BotType.ASAP]: "descriptions.dca_bot",
  [BotType.GRID]: "descriptions.grid_bot",
  [BotType.TRADING_VIEW]: "descriptions.trading_view_bot",
  [BotType.TRADING_VIEW_SIGNALS]: "",
  [BotType.HEDGING]: "descriptions.hedging_bot",
};

export const BOT_TYPE_TO_YOUTUBE_EMBED_LINK: Record<BotType, string> = {
  [BotType.SIGNAL]:
    "https://www.youtube.com/embed/_PObjxGyEHI?si=-fl4oLinRA5A0BWX",
  [BotType.ASAP]:
    "https://www.youtube.com/embed/Mtw_DjLXJcY?si=IoBOZMcSVBHWUtM2",
  [BotType.GRID]:
    "https://www.youtube.com/embed/oonfYHeDuaw?si=UwI4posX87sqdyaW",
  [BotType.TRADING_VIEW]:
    "https://www.youtube.com/embed/9T_m4Cu4x4o?si=2FJab5YpCQKFF1Pi",
  [BotType.TRADING_VIEW_SIGNALS]:
    "https://www.youtube.com/embed/9T_m4Cu4x4o?si=2FJab5YpCQKFF1Pi",
  [BotType.HEDGING]: "",
};

export const BOT_TYPE_TO_MAIN_ARTICLE_ID: Record<BotType, string> = {
  [BotType.SIGNAL]: "8800202",
  [BotType.ASAP]: "6166394",
  [BotType.GRID]: "8253570",
  [BotType.TRADING_VIEW]: "6248036",
  [BotType.TRADING_VIEW_SIGNALS]: "6248036",
  [BotType.HEDGING]: "",
};

export const BOT_TYPE_TO_ARTICLE_DATA = {
  [BotType.SIGNAL]: [
    {
      title: "articles.signals.what_is.title",
      description: "articles.signals.what_is.description",
      id: "8800202",
    },
    {
      title: "articles.signals.how_to_use.title",
      description: "articles.signals.how_to_use.description",
      id: "8800211",
    },
    {
      title: "articles.signals.advanced_settings.title",
      description: "articles.signals.advanced_settings.description",
      id: "8800218",
    },
  ],
  [BotType.ASAP]: [
    {
      title: "articles.dca.overview.title",
      description: "articles.dca.overview.description",
      id: "6166394",
    },
    {
      title: "articles.dca.create.title",
      description: "articles.dca.create.description",
      id: "6164330",
    },
    {
      title: "articles.dca.copy_public.title",
      description: "articles.dca.copy_public.description",
      id: "6165998",
    },
  ],
  [BotType.GRID]: [
    {
      title: "articles.grid.what_is.title",
      description: "articles.grid.what_is.description",
      id: "8253570",
    },
    {
      title: "articles.grid.create.title",
      description: "articles.grid.create.description",
      id: "8116944",
    },
    {
      title: "articles.grid.advanced_settings.title",
      description: "articles.grid.advanced_settings.description",
      id: "8238208",
    },
  ],
  [BotType.TRADING_VIEW]: [
    {
      title: "articles.trading_view.what_is.title",
      description: "articles.trading_view.what_is.description",
      id: "10751039",
    },
    {
      title: "articles.trading_view.create.title",
      description: "articles.trading_view.create.description",
      id: "6248036",
    },
    {
      title: "articles.trading_view.alerts.title",
      description: "articles.trading_view.alerts.description",
      id: "6325118",
    },
  ],
  [BotType.HEDGING]: [],
};

export const DRAWER_BACKTESTING_QUERY_PARAM = "drawer-backtesting";
export const APPLIED_BACKTESTING_QUERY_PARAM = "backtesting";
export const BACKTESTING_BOT_QUERY_PARAM = "backtesting-bot";
export const GROUP_ID_QUERY_PARAM = "group-id";
export const COPY_TO_WORKSPACE_QUERY_PARAM = "copy-to-workspace";
export const BOT_RANK_QUERY_PARAM = "bot-rank";
export const ACCOUNT_ID_QUERY_PARAM = "account-id";

export const MOBILE_LAYOUT = 650;
export const BOT_FORM_BOTTOM_SPACE = 40;

export const NEW_BOT_ID_QUERY_PARAM = "new-bot";
